import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { Accordion, AccordionTitleDetail, Button, Flex, Table } from '../../../../../components/shared';
import { FormikProps } from 'formik';
import { Option, PeopleProfileInstitutionFormType } from '../../../../../utils/types';
import { Edit2, X } from 'react-feather';
import { FiCheckCircle } from 'react-icons/fi';
import { useEditCommunityModalForm } from './EditCommunityModalForm';
import { getPeopleProfileInstitutionFormTypeDefault } from '../../../../../utils/format';
import { usePromptModalForm2 } from '../../../../../components/shared/promptModalForm';
import { client } from '../../../../../apollo';
import { ALL_GRADES, ALL_INSTITUTION_TYPES, ALL_STAKEHOLDER_TYPES } from '../../../../../graphql';
import { getAllGrades, getAllInstitutionTypes, getAllPeopleTypes } from '../../../../../utils/idbFns';
import { FormType } from '../FieldsForStudent';

export type InstitutionCardValuesType = {
  typeId: Option | undefined;
  portal: string;

  emisNumber: number | undefined;
  associatedInstitutions: PeopleProfileInstitutionFormType[];
};

export type CommunitiesCardPropsType = {
  formikProps: FormikProps<FormType>;
  viewMode?: boolean;
  currentInstitutionsOnly?: boolean;
  isLeaner?: boolean;
};

const CommunitiesCard: FC<CommunitiesCardPropsType> = props => {
  const formikProps = props.formikProps;
  const values = formikProps.values;
  const viewMode = props.viewMode === true;
  const currentInstitutionsOnly = props.currentInstitutionsOnly === true;

  const [EditCommunityModalForm, showEditCommunityModalForm] = useEditCommunityModalForm();
  const [PromptModalForm, showPromptModalForm] = usePromptModalForm2();
  const [allInstitutionTypes, setAllInstitutionTypes] = useState<any[]>([]);
  const [allStakeholderTypes, setAllStakeholderTypes] = useState<any[]>([]);
  const [allGrades, setAllGrades] = useState<any[]>([]);
  const mounted = useRef(true);

  const fetchEcdServiceTypes = async () => {
    try {
      const {
        data: { institutionTypes },
      }: any = await client.query({
        query: ALL_INSTITUTION_TYPES,
        variables: { input: {} },
      });

      if (institutionTypes && mounted.current) {
        const result = institutionTypes.map((a: any) => ({ label: a.name, value: a.id }));
        setAllInstitutionTypes(result);
      }
    } catch (error) {
      const result = await getAllInstitutionTypes();
      const institutionTypes = result.map((a: any) => ({ label: a.name, value: a.id }));
      setAllInstitutionTypes(institutionTypes);
    }
  };

  const fetchStakeholderTypes = async () => {
    try {
      const {
        data: { stakeholderTypes },
      }: any = await client.query({
        query: ALL_STAKEHOLDER_TYPES,
        variables: {
          input: {},
        },
      });

      if (stakeholderTypes && mounted.current) {
        const result = stakeholderTypes.map((a: any) => ({ label: a.name, value: a.id }));
        setAllStakeholderTypes(result);
      }
    } catch (error) {
      const result = await getAllPeopleTypes();
      const allPeopleTypes = result.map((a: any) => ({ label: a.name, value: a.id }));
      setAllStakeholderTypes(allPeopleTypes);
    }
  };

  const fetchGrades = async () => {
    try {
      const {
        data: { grades },
      }: any = await client.query({
        query: ALL_GRADES,
        variables: { input: {} },
      });

      if (grades && mounted.current) {
        const result = grades.map((a: any) => ({ label: a.value, value: a.id }));
        setAllGrades(result);
      }
    } catch (error) {
      const result = await getAllGrades();
      const grades = result.map((a: any) => ({ label: a.value, value: a.id }));
      setAllGrades(grades);
    }
  };

  useEffect(() => {
    fetchEcdServiceTypes();
    fetchStakeholderTypes();
    fetchGrades();

    return () => {
      mounted.current = false;
    };
  }, []);

  const COLUMNS = [
    {
      id: 'primary',
      Header: 'Primary',
      //width: '60px',
      accessor: (row: PeopleProfileInstitutionFormType) => (
        <Fragment>{row.primary && <FiCheckCircle color="#4C9A5C" fontSize="1.5em" strokeWidth={3} />}</Fragment>
      ),
    },
    {
      id: 'name',
      Header: 'Name',
      accessor: 'institution.name',
    },
    {
      id: 'roleType',
      Header: 'Role Type',
      accessor: 'roleId.label',
    },
    ...(viewMode
      ? []
      : [
          {
            id: 'actions',
            accessor: (row: PeopleProfileInstitutionFormType) => (
              <Flex direction="row" size="1" justify="flex-end">
                <Button
                  bg="warning"
                  icon={<Edit2 />}
                  isIcon={true}
                  mr={1}
                  p={0}
                  type="button"
                  width="40px"
                  onClick={() => {
                    onEdit(row);
                  }}
                />
                <Button
                  bg="error"
                  icon={<X />}
                  isIcon={true}
                  mr={1}
                  p={0}
                  type="button"
                  width="40px"
                  onClick={() => {
                    onDelete(row);
                  }}
                />
              </Flex>
            ),
          },
        ]),
  ];

  const fixupPrimaries = (values: PeopleProfileInstitutionFormType[], changed: PeopleProfileInstitutionFormType) => {
    if (changed.primary) {
      values.forEach(value => {
        if (value.id !== changed.id) {
          // only one primary per person/roletype(stakeholder type)
          if (value.primary && value.typeId && changed.typeId && value.typeId.value === changed.typeId.value) {
            value.primary = false;
          }
        }
      });
    }
  };

  const onNew = () => {
    values['nosubmit'] = true;
    showEditCommunityModalForm(
      getPeopleProfileInstitutionFormTypeDefault(),
      values,
      (cancel: boolean, data: PeopleProfileInstitutionFormType) => {
        values['nosubmit'] = false;
        if (!cancel) {
          if (data.primary) {
            formikProps.setFieldValue('communityTypeId', data.institution?.communityType as any);
            formikProps.setFieldValue('provinceId', data.institution?.province as any);
            formikProps.setFieldValue('districtId', data.institution?.district as any);
            formikProps.setFieldValue('localMunicipalityId', data.institution?.localMunicipality as any);
            formikProps.setFieldValue('districtMunicipalityId', data.institution?.districtMunicipality as any);
            formikProps.setFieldValue('roleId', data.roleId);
            formikProps.setFieldValue('dateExited', data.dateExited);
            formikProps.setFieldValue('terminationId', data.reasonForTerminationId);
          }

          values.associatedInstitutions.push(data);
          fixupPrimaries(values.associatedInstitutions, data);
        }
      },
      props.isLeaner,
      values?.typeId,
      allInstitutionTypes,
      allStakeholderTypes,
      allGrades
    );
  };

  const onEdit = (item: PeopleProfileInstitutionFormType) => {
    values['nosubmit'] = true;
    showEditCommunityModalForm(
      item,
      values,
      (cancel: boolean, data: PeopleProfileInstitutionFormType) => {
        values['nosubmit'] = false;
        if (!cancel) {
          if (data.primary) {
            formikProps.setFieldValue('communityTypeId', data.institution?.communityType as any);
            formikProps.setFieldValue('provinceId', data.institution?.province as any);
            formikProps.setFieldValue('districtId', data.institution?.district as any);
            formikProps.setFieldValue('localMunicipalityId', data.institution?.localMunicipality as any);
            formikProps.setFieldValue('districtMunicipalityId', data.institution?.districtMunicipality as any);
            formikProps.setFieldValue('roleId', data.roleId);
            formikProps.setFieldValue('dateExited', data.dateExited);
            formikProps.setFieldValue('terminationId', data.reasonForTerminationId);
          }

          const index = values.associatedInstitutions.findIndex(x => x.id === data.id);
          if (index >= 0) {
            values.associatedInstitutions[index] = data;
            fixupPrimaries(values.associatedInstitutions, data);
          }
        }
      },
      props.isLeaner,
      values?.typeId,
      allInstitutionTypes,
      allStakeholderTypes,
      allGrades
    );
  };

  const onDelete = (item: PeopleProfileInstitutionFormType) => {
    const index = values.associatedInstitutions.findIndex(x => x.id === item.id);
    if (index >= 0) {
      values['nosubmit'] = true;
      showPromptModalForm({
        id: item.id,
        heading: 'Delete',
        buttons: 'delete',
        onDone: (promptId: string, button: string, cancelled: boolean, id: string) => {
          values['nosubmit'] = false;
          if (!cancelled) {
            values.associatedInstitutions.splice(index, 1);
          }
        },
      });
    }
  };

  const getData = (
    data: PeopleProfileInstitutionFormType[],
    currentOnly: boolean
  ): PeopleProfileInstitutionFormType[] => {
    const now = new Date();
    if (currentOnly) {
      return data.filter(x => x.startDate <= now && now <= x.endDate);
    }
    return data;
  };

  return (
    <Fragment>
      <EditCommunityModalForm />
      <PromptModalForm />

      <Accordion
        key={'associatedInstitutions'}
        title={`Communities (${values.associatedInstitutions.length})`}
        titleDetail={
          <Fragment>
            {!viewMode && (
              <AccordionTitleDetail
                onClick={() => {
                  onNew();
                }}
                propagateOnClick={false}
                text={'+ New'}
              />
            )}
          </Fragment>
        }
      >
        <Table
          defaultPageSize={5}
          padding={0}
          showHeader={false}
          loading={false}
          hideActionButton={true}
          columns={COLUMNS}
          data={getData(values.associatedInstitutions, currentInstitutionsOnly)}
        />
      </Accordion>
    </Fragment>
  );
};

export default CommunitiesCard;
